<template>
  <div class="project-details">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" icon-left="arrow-left-bold">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <b-dropdown v-if="!isEditMode" class="column is-narrow" :triggers="['hover']" aria-role="list">
                  <template #trigger>
                    <b-button icon-right="menu-down">
                      {{ $t('common.actions') }}
                    </b-button>
                  </template>
                  <b-dropdown-item v-if="(!isTemplateMode && this.currentUserAccess('Projects', UserActions.UPDATE, this.id))
                                        || (isTemplateMode && this.currentUserAccess('ProjectTemplates', UserActions.UPDATE))"
                                   aria-role="listitem" @click="editClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="pencil"></b-icon>
                      {{ $t('common.edit') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                      v-if="!isTemplateMode && this.currentUserAccess('ProjectLogs', UserActions.VIEW, this.id)"
                      aria-role="listitem" @click="worklogClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="eye"></b-icon>
                      {{ $t('projects-view.enter-worklog') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                      v-if="!isTemplateMode && this.currentUserAccess('ProjectSummary', UserActions.VIEW, this.id)"
                      aria-role="listitem" @click="summaryClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="chart-gantt"></b-icon>
                      {{ $t('projects-view.show-summary') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                      v-if="!isTemplateMode && this.currentUserAccess('ProjectDetails', UserActions.VIEW, this.id)"
                      aria-role="listitem" @click="exportClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="file-download"></b-icon>
                      {{ $t('projects-view.export') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                      v-if="!isTemplateMode && this.currentUserAccess('ProjectTemplates', UserActions.UPDATE)"
                      aria-role="listitem" @click="saveAsTemplateClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="content-duplicate"></b-icon>
                      {{ $t('projects-view.save-as-template') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="isTemplateMode && this.currentUserAccess('ProjectDetails', UserActions.CREATE)"
                                   aria-role="listitem" @click="saveAsProjectClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="android-studio"></b-icon>
                      {{ $t('projects-view.create-project-from-template') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="(!isTemplateMode && this.currentUserAccess('Projects', UserActions.DELETE, this.id))
                                        || (isTemplateMode && this.currentUserAccess('ProjectTemplates', UserActions.DELETE))"
                                   aria-role="listitem" @click="deleteClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="delete"></b-icon>
                      {{ $t('common.delete') }}
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
                <div
                    v-if="!isLoading && !isTemplateMode && !isInNewMode && !isProjectArchived && isEditMode && this.currentUserAccess('ProjectDetails', UserActions.DELETE, this.id)"
                    class="column is-narrow">
                  <b-button type="is-danger"
                            icon-left="archive-arrow-down" @click="archiveClicked">
                    {{ $t('project-details-view.archive-project-button') }}
                  </b-button>
                </div>
                <div
                    v-else-if="this.currentUserAccess('ProjectDetails', UserActions.DELETE) && !isLoading && !isTemplateMode && !isInNewMode && isProjectArchived && isEditMode"
                    class="column is-narrow">
                  <b-button @click="unArchiveClicked" icon-left="archive-arrow-up-outline">
                    {{ $t('project-details-view.unarchive-project-button') }}
                  </b-button>
                </div>
              </div>
              <b-message v-if="isTemplateMode" type="is-info" has-icon>
                {{ $t('project-details-view.seeing-template-notice') }}
              </b-message>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-half">
                  <b-field v-if="isEditMode" :label="$t('project-details-view.project-code-field')">
                    <b-input v-model="projectData.code" tabindex="1"
                             :placeholder="$t('project-details-view.project-code-field')"
                    ></b-input>
                  </b-field>
                  <b-field :label="$t('project-details-view.project-title-field')">
                    <b-input v-model="projectData.title" tabindex="2"
                             :placeholder="$t('project-details-view.project-title-field')" v-if="isEditMode"></b-input>
                    <label v-else>{{ projectData.title }}</label>
                  </b-field>
                  <b-field v-if="!isTemplateMode" :label="$t('project-details-view.project-manager-field')">
                    <b-taginput
                        v-if="isEditMode && this.currentUserAccess('Users', UserActions.UPDATE)"
                        v-model="projectManagersTags"
                        :data="filteredProjectManagers"
                        :loading="isFetching"
                        :has-counter="false"
                        :open-on-focus="true"
                        autocomplete
                        field="email"
                        icon="account-box"
                        :remove-on-keys=[]
                        :placeholder="$t('project-details-view.project-manager-placeholder')"
                        tabindex="3"
                        @typing="getFilteredProjectManagers"
                        @input="onProjectManagersInput">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium"
                             v-for="projectManager in projectManagersTags"
                             v-bind:key="projectManager.name.first">
                        {{ getFullName(projectManager.name) }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field v-if="!isTemplateMode" :label="$t('project-details-view.site-managers-field')">
                    <b-taginput
                        v-if="isEditMode"
                        v-model="siteManagersTags"
                        :data="filteredSiteManagers"
                        :loading="isFetching"
                        :open-on-focus="true"
                        autocomplete
                        field="email"
                        icon="account-box"
                        :remove-on-keys=[]
                        :placeholder="$t('project-details-view.site-managers-placeholder')"
                        tabindex="4"
                        @typing="getFilteredSiteManagers"
                        @input="onSiteManagersInput">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium"
                             v-for="siteManager in siteManagersTags"
                             v-bind:key="siteManager.name.first">
                        {{ getFullName(siteManager.name) }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('project-details-view.project-description-field')">
                    <b-input v-if="isEditMode" v-model="projectData.description" tabindex="5"
                             :placeholder="$t('project-details-view.project-description-field')"
                             type="textarea"></b-input>
                    <label v-else style="white-space: pre-wrap;">{{ projectData.description }}</label>
                  </b-field>
                </div>
                <div class="column is-half">
                  <b-field v-if="!isTemplateMode" :label="$t('project-details-view.project-status-field')">
                    <b-select v-if="isEditMode" v-model="projectData.phase" tabindex="6"
                              :placeholder="$t('project-details-view.project-status-placeholder')">
                      <option
                          v-for="option in projectPhaseOptions"
                          :value="option.id"
                          :key="option.id">
                        {{ $t(option.translationKey) }}
                      </option>
                    </b-select>
                    <label v-else>
                      {{ projectStatus }}
                    </label>
                  </b-field>
                  <div class="notification pt-5 mt-5">
                    <div class="columns" v-if="isEditMode && !isAdvancedEditMode && !isTemplateMode && !isInNewMode">
                      <div class="column is-half">
                        <b-button class="button is-dark" @click="advancedEditClicked" tabindex="12" icon-left="pencil">
                          {{ $t('project-details-view.advanced-edit-details-button') }}
                        </b-button>
                      </div>
                    </div>
                    <div class="columns">
                      <div class="column is-half">
                        <b-tooltip :label="$t('project-details-view.internal-personal-cost-tooltip')"
                                   position="is-right">
                          <b-field :label="$t('project-details-view.internal-personal-cost-field')">
                            <b-input v-model="projectData.internal_personal_cost" tabindex="7" type="number" min="0"
                                     step="1"
                                     :placeholder="$t('project-details-view.internal-personal-cost-field')"
                                     v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"></b-input>
                            <label v-else>{{
                                getFormattedPricePerDuration(projectData.internal_personal_cost, "hour")
                              }}</label>
                          </b-field>
                        </b-tooltip>
                      </div>
                      <div class="column is-half">
                        <b-tooltip :label="$t('project-details-view.internal-personal-hours-tooltip')"
                                   position="is-right">
                          <b-field :label="$t('project-details-view.internal-personal-hours-field')">
                            <b-input v-model="projectData.internal_personal_day_in_hours" tabindex="8" type="number"
                                     min="0" max="24" step="0.5"
                                     :placeholder="$t('project-details-view.internal-personal-hours-field')"
                                     v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"></b-input>
                            <label v-else>{{ getFormattedTimeHour(projectData.internal_personal_day_in_hours) }}</label>
                          </b-field>
                        </b-tooltip>
                      </div>
                    </div>
                    <div class="columns">
                      <div class="column is-half">
                        <b-tooltip :label="$t('project-details-view.external-personal-cost-tooltip')"
                                   position="is-right">
                          <b-field :label="$t('project-details-view.external-personal-cost-field')">
                            <b-input v-model="projectData.external_personal_cost" tabindex="9" type="number" min="0"
                                     step="1"
                                     :placeholder="$t('project-details-view.external-personal-cost-field')"
                                     v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"></b-input>
                            <label v-else>{{
                                getFormattedPricePerDuration(projectData.external_personal_cost, "hour")
                              }}</label>
                          </b-field>
                        </b-tooltip>
                      </div>
                      <div class="column is-half">
                        <b-tooltip :label="$t('project-details-view.external-personal-hours-tooltip')"
                                   position="is-right">
                          <b-field :label="$t('project-details-view.external-personal-hours-field')">
                            <b-input v-model="projectData.external_personal_day_in_hours" tabindex="10" type="number"
                                     min="0" max="24" step="0.5"
                                     :placeholder="$t('project-details-view.external-personal-hours-field')"
                                     v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"></b-input>
                            <label v-else>{{ getFormattedTimeHour(projectData.external_personal_day_in_hours) }}</label>
                          </b-field>
                        </b-tooltip>
                      </div>
                    </div>
                  </div>
                  <div class="columns">
                    <ImagePicker v-bind:is-edit-mode="this.isEditMode" v-bind:image-url="this.projectPicUrl"
                                 @onImageFileRemoveSelected="onProjectPicFileRemoveSelected"
                                 @onImageFileSelected="onProjectPicFileSelected" style="margin-left: 0"/>
                  </div>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" tabindex="11" icon-left="close-thick">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button v-if="(!isTemplateMode && ((isInNewMode && currentUserAccess('ProjectDetails', this.UserActions.CREATE, this.id)) || currentUserAccess('ProjectDetails', this.UserActions.UPDATE, this.id)))
                              || (isTemplateMode && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE))"
                            class="button is-primary" @click="saveClicked" tabindex="12" icon-left="content-save">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
            <div v-if="!isInNewMode" class="box">
              <b-tabs type="is-boxed" v-model="activeTab">
                <b-tab-item :label="$t('tasks-view.component-title')" icon="format-list-checks">
                  <Tasks v-bind:project_id="this.id" v-bind:is_template="isTemplateMode"/>
                </b-tab-item>
                <b-tab-item :label="$t('personnel-view.component-title')" icon="account-hard-hat">
                  <Personnel v-bind:project_id="this.id" v-bind:is_template="isTemplateMode"/>
                </b-tab-item>
                <b-tab-item :label="$t('equipments-view.component-title')" icon="robot-industrial">
                  <Equipments v-bind:project_id="this.id" v-bind:is_template="isTemplateMode"/>
                </b-tab-item>
                <b-tab-item :label="$t('consumables-view.component-title')" icon="cart-variant">
                  <Consumables v-bind:project_id="this.id" v-bind:is_template="isTemplateMode"/>
                </b-tab-item>
                <b-tab-item v-if="!isTemplateMode" :label="$t('files-view.component-title')"
                            icon="text-box-multiple-outline">
                  <FilesList v-bind:project_id="this.id"/>
                </b-tab-item>
              </b-tabs>
            </div>
            <div class="box">
              <Comments v-bind:document_id="this.id"/>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";
import Tasks from '@/components/Tasks';
import Personnel from '@/components/Personnel';
import Equipments from '@/components/Equipments';
import Consumables from '@/components/Consumables';
import Comments from "@/components/Comments";
import ImagePicker from "@/components/ImagePicker";
import FilesList from "@/components/FilesList";

export default {
  name: "ProjectDetails",
  components: {
    FilesList,
    Tasks,
    Personnel,
    Equipments,
    Consumables,
    Comments,
    ImagePicker
  },
  props: ['id'],
  data() {
    return {
      projectData: {
        phase: "initiation"
      },
      isLoading: false,
      siteManagersTags: [],
      projectManagersTags: [],
      projectManagersData: [],
      siteManagersData: [],
      filteredProjectManagers: [],
      filteredSiteManagers: [],
      isFetching: false,
      isEditMode: false,
      isAdvancedEditMode: false,
      prevRoute: null,
      activeTab: 0,
      projectPicFile: null,
      projectPicUrl: null
    }
  },
  methods: {
    createProject: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "insert_project";
      insertProjectFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create project result: ", result.data);
        this.showSuccess(this.$t('messages.project-created'));
        this.backClicked()
      }).catch((error) => {
        this.isLoading = false
        this.showError("Error writing document: " + error);
      });
    },
    updateProject: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const updateProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "update_project";
      console.log("updateData: ", updateData);
      updateProjectFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("result: ", result.data);
        this.showSuccess(this.$t('messages.project-updated'));
        if (result.data.payload !== undefined && result.data.payload["projects"].updated[0].is_archived) {
          this.projectData.is_archived = result.data.payload["projects"].updated[0].is_archived["current"]
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error updating project: ", error);
      });
      this.updateProjectPicture()
    },
    downloadProjectPicUrl: function () {
      this.projectPicFile = null
      this.projectPicUrl = null
      this.getProjectPicRef().getDownloadURL().then((downloadURL) => {
        console.log('projectPic available at', downloadURL);
        this.projectPicUrl = downloadURL
      }).catch((error) => {
        console.log('projectPic error: ', error.message);
      });
    },
    updateProjectPicture: function () {
      if (this.projectPicFile !== null && this.projectPicFile !== undefined) {
        this.projectPicUrl = ""
        this.getProjectPicRef().put(this.projectPicFile).then((snapshot) => {
          console.log('Project pic is uploaded: ', snapshot.metadata);
          this.downloadProjectPicUrl()
        }).catch((error) => {
          this.showError(error.message)
          console.log('Project pic upload error: ', error.message);
          this.downloadProjectPicUrl()
        });
      } else {
        console.log("No project pic is selected!")
      }
    },
    getProjectPicRef: function () {
      const imagePath = this.getStorageRootFolder() + '/projects/' + this.id + '/projectPic'
      console.log("image ref: ", imagePath)
      return firebase.storage().ref(imagePath)
    },
    onProjectPicFileSelected: function (selectedImageFile) {
      this.projectPicFile = selectedImageFile
      this.projectPicUrl = URL.createObjectURL(this.projectPicFile)
      console.log("Selected file: ", this.projectPicUrl)
    },
    onProjectPicFileRemoveSelected() {
      this.downloadProjectPicUrl()
    },
    deleteProject: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const deleteProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteProjectFunction({
        action: "delete_project",
        document_id: this.id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete project result: ", result.data);
        this.showSuccess(this.$t('messages.project-deleted'));
        this.$emit('projectDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result: ", error);
      });
    },
    readProjectData: function () {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const getProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectFunction({action: "get_project_by_id", document_id: this.id, fill_meta: true})
          .then((result) => {
            this.isLoading = false
            console.log("result: ", result.data);
            this.projectData = result.data["projects"];
            this.siteManagersTags = this.projectData.meta["site_managers"]
            this.projectManagersTags = this.projectData.meta["managers"]
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            if (error.message === "InvalidPath") {
              this.$router.replace("../projects");
            }
            console.log("Error getting project: ", error);
          });
    },
    readUsersData: function () {
      this.isFetching = true
      const functions = firebase.app().functions("europe-west3");
      const getUsersFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUsersFunction({action: "get_users"})
          .then((result) => {
            this.isFetching = false
            console.log("users result: ", result.data);
            // all users except inactive ones
            const allActiveUsersData = result.data["users"].filter(function (el) {
              return el.roles.includes("inactive") !== true;
            });
            // active users who are admin or project_manager can be a project manager
            this.projectManagersData = allActiveUsersData.filter(function (el) {
              return el.roles.includes("site_manager") !== true;
            });
            // every active user could be a site manager
            this.siteManagersData = allActiveUsersData
            this.filteredProjectManagers = this.projectManagersData
            this.filteredSiteManagers = this.siteManagersData
            // setting the current user as project manager when creating a new project
            if (this.isInNewMode) {
              const currentUser = JSON.parse(localStorage.getItem("currentUser"))
              this.projectManagersTags = allActiveUsersData.filter(function (el) {
                return el.email === currentUser.email;
              });
            }
          })
          .catch((error) => {
            this.isFetching = false
            this.showError(error.message)
            console.log("Error getting users: ", error);
          });
    },
    getFilteredProjectManagers(text) {
      this.filteredProjectManagers = this.projectManagersData.filter((option) => {
        return option.email
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    getFilteredSiteManagers(text) {
      this.filteredSiteManagers = this.siteManagersData.filter((option) => {
        return option.email
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    archiveProject() {
      const dataFieldsToUpdate = {
        document_id: this.id,
        is_archived: true
      }
      this.updateProject(dataFieldsToUpdate);
    },
    onProjectManagersInput() {
      this.filteredProjectManagers = this.projectManagersData
    },
    onSiteManagersInput() {
      this.filteredSiteManagers = this.siteManagersData
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      if (this.isTemplateMode) {
        this.$buefy.dialog.confirm({
          title: this.$t('templates-view.delete-template-title').toString(),
          message: this.$t('templates-view.delete-template-message').toString(),
          confirmText: this.$t('templates-view.delete-template-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-danger',
          hasIcon: true,
          icon: 'delete',
          onConfirm: () => {
            this.deleteProject();
          }
        });
      } else if (!this.projectData.is_archived) {
        this.$buefy.dialog.alert({
          title: this.$t('project-details-view.delete-confirm-title').toString(),
          message: this.$t('project-details-view.delete-unarchived-project-message').toString(),
          type: 'is-warning',
          hasIcon: true,
          icon: 'alert',
        });
      } else {
        this.$buefy.dialog.confirm({
          title: this.$t('project-details-view.delete-confirm-title').toString(),
          message: this.$t('project-details-view.delete-confirm-message').toString(),
          confirmText: this.$t('project-details-view.delete-confirm-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-danger',
          hasIcon: true,
          icon: 'delete',
          onConfirm: () => {
            this.deleteProject();
          }
        });
      }
    },
    advancedEditClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('project-details-view.advanced-edit-confirm-title').toString(),
        message: this.$t('project-details-view.advanced-edit-confirm-message').toString(),
        confirmText: this.$t('project-details-view.advanced-edit-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-warning',
        hasIcon: true,
        icon: 'alert',
        onConfirm: () => this.isAdvancedEditMode = true
      });
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$router.back()
      } else if (this.isEditMode) {
        this.readProjectData();
        this.downloadProjectPicUrl()
      }
      this.isEditMode = !this.isEditMode;
      this.isAdvancedEditMode = false;
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        code: this.projectData.code,
        title: this.projectData.title,
        description: this.projectData.description,
        phase: (this.projectData.phase === null) ? this.projectPhaseOptions[0].id : this.projectData.phase,
        site_manager_ids: this.siteManagersTags.map(user => user.document_id),
        manager_ids: this.projectManagersTags.map(user => user.document_id),
        internal_personal_cost: (this.projectData.internal_personal_cost === "") ? 0 : parseInt(this.projectData.internal_personal_cost),
        internal_personal_day_in_hours: (this.projectData.internal_personal_day_in_hours === "") ? 0 : parseInt(this.projectData.internal_personal_day_in_hours),
        external_personal_cost: (this.projectData.external_personal_cost === "") ? 0 : parseInt(this.projectData.external_personal_cost),
        external_personal_day_in_hours: (this.projectData.external_personal_day_in_hours === "") ? 0 : parseInt(this.projectData.external_personal_day_in_hours)
      }

      if (this.isInNewMode) {
        this.createProject(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate["document_id"] = this.id
        this.updateProject(dataFieldsToUpdate);
      }
      this.isAdvancedEditMode = false;
    },
    worklogClicked: function () {
      this.$router.push({
        name: 'ProjectLogs',
        params: {
          project_id: this.id
        }
      })
    },
    summaryClicked: function () {
      this.$router.push({
        name: 'ProjectSummary',
        params: {
          project_id: this.id
        }
      })
    },
    exportClicked: function () {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const exportProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      exportProjectFunction({action: "export_project", document_id: this.id})
          .then((result) => {
            this.isLoading = false
            console.log("export project result: ", result.data);
            if (result.data.url !== null && result.data.url !== "" && result.data.url !== undefined) {
              this.$buefy.dialog.alert({
                title: this.$t('project-details-view.export-ready-title').toString(),
                message: result.data.url,
                confirmText: this.$t('project-details-view.export-ready-done-button').toString(),
                cancelText: this.$t('common.cancel').toString()
              })
            }
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message)
            console.log("Error exporting project: ", error);
          });
    },
    saveAsTemplateClicked: function () {
      this.$buefy.dialog.prompt({
        message: this.$t('project-details-view.save-as-template-title').toString(),
        inputAttrs: {
          type: 'text',
          placeholder: this.$t('project-details-view.template-code-placeholder').toString()
        },
        confirmText: this.$t('common.save').toString(),
        cancelText: this.$t('common.cancel').toString(),
        trapFocus: true,
        closeOnConfirm: false,
        onConfirm: (value, {close}) => {
          this.$buefy.toast.open(this.$t('project-details-view.saving-template-title').toString())
          const functions = firebase.app().functions("europe-west3");
          const saveAsTemplateFunction = functions.httpsCallable(this.getSubmitEndpoint());
          const payload = {action: "copy_project", document_id: this.id, code: value, save_as: "template"}
          saveAsTemplateFunction(payload)
              .then((result) => {
                console.log("saved template results:", result.data);
                this.showSuccess(this.$t('project-details-view.save-template-success'))
                close()
              })
              .catch((error) => {
                console.log("Error saving template: ", error);
                this.showError(error.message)
              })
        }
      })
    },
    saveAsProjectClicked: function () {
      this.$buefy.dialog.prompt({
        message: this.$t('templates-view.save-as-project-title').toString(),
        inputAttrs: {
          type: 'text',
          placeholder: this.$t('templates-view.project-code-placeholder').toString()
        },
        confirmText: this.$t('common.save').toString(),
        cancelText: this.$t('common.cancel').toString(),
        trapFocus: true,
        closeOnConfirm: false,
        onConfirm: (value, {close}) => {
          this.isLoading = true
          const functions = firebase.app().functions("europe-west3");
          const saveAsProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
          const payload = {action: "copy_project", document_id: this.id, code: value, save_as: "project"}
          saveAsProjectFunction(payload)
              .then((result) => {
                this.isLoading = false
                close()
                console.log("saved project results:", result.data);
                this.showSuccess(this.$t('templates-view.save-project-success'))
                const insertedProjectId = result.data.payload["projects"].inserted[0].document_id
                if (insertedProjectId !== undefined) {
                  this.$router.replace({
                    name: 'ProjectDetails',
                    params: {
                      id: insertedProjectId
                    }
                  }).catch(() => {
                  });
                }
              })
              .catch((error) => {
                this.isLoading = false
                this.showError(error.message)
                console.log("Error saving project: ", error);
              })
        }
      })
    },
    archiveClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('project-details-view.archive-project-confirm-title').toString(),
        message: this.$t('project-details-view.archive-project-confirm-message').toString(),
        confirmText: this.$t('project-details-view.archive-project-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'archive-arrow-down',
        onConfirm: () => this.archiveProject(),
      });
    },
    unArchiveClicked: function () {
      const dataFieldsToUpdate = {
        document_id: this.id,
        is_archived: false
      }
      this.updateProject(dataFieldsToUpdate);
    },
    backClicked: function () {
      this.$router.back()
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },
  mounted() {
    if ((!this.isTemplateMode && !this.currentUserAccess("ProjectDetails", this.UserActions.VIEW, this.id))
        || (this.isTemplateMode
            && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    if (this.prevRoute != null) {
      if (this.prevRoute.path.includes("personnel")) {
        this.activeTab = 1;
      } else if (this.prevRoute.path.includes("equipments")) {
        this.activeTab = 2;
      } else if (this.prevRoute.path.includes("consumables")) {
        this.activeTab = 3;
      }
    }
    this.readUsersData();
    if (this.isInNewMode) {
      console.log("creating new project");
      this.isEditMode = true;
    } else {
      console.log("properties: ", this.$route.params.id);
      this.readProjectData();
      this.downloadProjectPicUrl();
    }
  },
  computed: {
    isInNewMode() {
      return this.id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('project-details-view.new-project-title')
      } else if (this.isEditMode) {
        return (this.isTemplateMode ? this.$t('project-details-view.edit-template-title') : this.$t('project-details-view.edit-project-title')) + " " + this.projectData.code
      } else {
        return (this.isTemplateMode ? this.$t('project-details-view.view-template-title') : this.$t('project-details-view.view-project-title')) + " " + this.projectData.code
      }
    },
    isProjectArchived() {
      return this.projectData.is_archived
    },
    projectStatus() {
      if (!this.isEditMode) {
        const st = this.projectPhaseOptions.find(obj => {
          return obj.id === this.projectData.phase
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return "";
    },
    isTemplateMode() {
      return this.projectData.is_template
    }
  }
};
</script>

<style scoped>
</style>
